import {z} from 'zod'

import {tzDateFromIso} from 'src/api/zod-helpers'
import {mapStockSource, type StockSource, stockSourceRawFields} from 'src/equipment/stock-source'
import {type LoadDriver, loadDriverRawSchema, loadDriverSchema} from 'src/jobs/loads/schemas'

import {customerContactSourceSchema} from '../customers/schemas'

const driverJobLocationRawSchema = z.object({
    id: z.string(),
    name: z.string(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    notes: z.string().nullable(),
})

const driverJobAssetDeploymentRawSchema = z.object({
    status: z.enum(['pending', 'deployed', 'returned']),
    location_label: z.string().nullable(),
    deployed_at: z.string().nullable(),
    confirmed: z.boolean().optional(),
})

const driverStockDeliveredByLocationRawSchema = z.object({
    job_location_id: z.string(),
    location_name: z.string(),
    quantity: z.number(),
})

const driverStockEquipmentGroupRawSchema = z.object({
    ...stockSourceRawFields,
    rental_asset_type_id: z.string(),
    rental_asset_type_name: z.string(),
    assigned_quantity: z.number(),
    delivered_quantity: z.number(),
    remaining_quantity: z.number(),
    delivered_by_location: z.array(driverStockDeliveredByLocationRawSchema),
    returned_quantity: z.number(),
})

const driverJobAssetAssignmentRawSchema = z.object({
    id: z.string(),
    rental_asset_id: z.string(),
    rental_asset_name: z.string(),
    deployment: driverJobAssetDeploymentRawSchema,
})

const driverJobCommentRawSchema = z.object({
    id: z.string(),
    content: z.string(),
    created_at: z.string(),
    created_by_name: z.string(),
})

const driverJobDocumentRawSchema = z.object({
    id: z.string(),
    name: z.string(),
    content_type: z.string(),
    size_bytes: z.number(),
})

const driverCustomerContactRawSchema = z.object({
    id: z.string(),
    name: z.string(),
    title: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
})

const driverLoadEntryRawSchema = z.object({
    id: z.string(),
    job_worker_assignment_id: z.string().nullable(),
    worker_name: z.string().nullable(),
    load_date: z.string(),
    load_count: z.number(),
    ticket_number: z.string().nullable(),
    voided: z.boolean().optional(),
    voided_at: z.string().nullable().optional(),
})

export const driverJobLoadsRawSchema = z.object({
    total_loads: z.number(),
    entries: z.array(driverLoadEntryRawSchema),
    drivers: z.array(loadDriverRawSchema),
})

export type DriverJobLoadsRaw = z.infer<typeof driverJobLoadsRawSchema>

export type DriverJobLoads = {
    totalLoads: number
    entries: Array<{
        id: string
        assignmentId: string | null
        workerName: string | null
        loadDate: string
        loadCount: number
        ticketNumber: string | null
        voided: boolean
        voidedAt: Date | null
    }>
    drivers: LoadDriver[]
}

export function mapDriverJobLoads(raw: DriverJobLoadsRaw): DriverJobLoads {
    return {
        totalLoads: raw.total_loads,
        entries: raw.entries.map((entry) => ({
            id: entry.id,
            assignmentId: entry.job_worker_assignment_id,
            workerName: entry.worker_name,
            loadDate: entry.load_date,
            loadCount: entry.load_count,
            ticketNumber: entry.ticket_number,
            voided: entry.voided ?? false,
            voidedAt: entry.voided_at ? tzDateFromIso.parse(entry.voided_at) : null,
        })),
        drivers: raw.drivers.map((driver) => loadDriverSchema.parse(driver)),
    }
}

export const driverJobRawSchema = z.object({
    id: z.string(),
    display_id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    service_type_id: z.string(),
    service_type_name: z.string(),
    project_description: z.string().nullable(),
    project_notes: z.string().nullable(),
    notes: z.string().nullable(),
    customer_name: z.string(),
    customer_contact: driverCustomerContactRawSchema.nullable().optional(),
    customer_contact_source: customerContactSourceSchema.nullable().optional(),
    start_date: z.string(),
    start_time: z.string().nullable(),
    end_date: z.string().nullable(),
    end_time: z.string().nullable(),
    date_tbc: z.boolean(),
    is_pickup: z.boolean(),
    billed_per_load: z.boolean(),
    loads: driverJobLoadsRawSchema.nullable(),
    locations: z.array(driverJobLocationRawSchema),
    asset_assignments: z.array(driverJobAssetAssignmentRawSchema),
    stock_equipment_groups: z.array(driverStockEquipmentGroupRawSchema).default([]),
    comments: z.array(driverJobCommentRawSchema),
    documents: z.array(driverJobDocumentRawSchema),
    driver_name: z.string().nullable(),
    job_worker_assignment_id: z.string().nullable(),
})

export type DriverJobRaw = z.infer<typeof driverJobRawSchema>

export type DriverCustomerContact = {
    id: string
    name: string
    title: string | null
    phone: string | null
    email: string | null
}

export type DriverJob = {
    id: string
    displayId: string
    name: string
    description: string | null
    serviceTypeId: string
    serviceTypeName: string
    projectDescription: string | null
    projectNotes: string | null
    notes: string | null
    customerName: string
    customerContact: DriverCustomerContact | null
    customerContactSource: 'job' | 'project' | null
    startDate: string
    startTime: string | null
    endDate: string | null
    endTime: string | null
    dateTbc: boolean
    isPickup: boolean
    loads: DriverJobLoads | null
    driverName: string | null
    /** Null when the link has no active assignment on the job. */
    driverAssignmentId: string | null
    locations: Array<{
        id: string
        name: string
        latitude: number | null
        longitude: number | null
        notes: string | null
    }>
    assetAssignments: Array<{
        id: string
        rentalAssetId: string
        rentalAssetName: string
        deployment: {
            status: 'pending' | 'deployed' | 'returned'
            locationLabel: string | null
            deployedAt: string | null
            /** False while dispatch has not confirmed an automatic rental went out. */
            confirmed: boolean
        }
    }>
    comments: Array<{
        id: string
        content: string
        createdAt: string
        createdByName: string
    }>
    documents: Array<{
        id: string
        name: string
        contentType: string
        sizeBytes: number
    }>
    stockEquipmentGroups: Array<
        StockSource & {
            rentalAssetTypeId: string
            rentalAssetTypeName: string
            assignedQuantity: number
            deliveredQuantity: number
            remainingQuantity: number
            returnedQuantity: number
            deliveredByLocation: Array<{
                jobLocationId: string
                locationName: string
                quantity: number
            }>
        }
    >
}

export const driverLinkRawSchema = z.object({
    worker_id: z.string(),
    worker_name: z.string(),
    url: z.string(),
    token: z.string(),
})

export type DriverLinkRaw = z.infer<typeof driverLinkRawSchema>

export type DriverLink = {
    workerId: string
    workerName: string
    url: string
}

export function mapDriverLink(raw: DriverLinkRaw): DriverLink {
    return {workerId: raw.worker_id, workerName: raw.worker_name, url: raw.url}
}

export function mapDriverJob(raw: DriverJobRaw): DriverJob {
    return {
        id: raw.id,
        displayId: raw.display_id,
        name: raw.name,
        description: raw.description,
        serviceTypeId: raw.service_type_id,
        serviceTypeName: raw.service_type_name,
        projectDescription: raw.project_description,
        projectNotes: raw.project_notes,
        notes: raw.notes,
        customerName: raw.customer_name,
        customerContact:
            raw.customer_contact == null
                ? null
                : {
                      id: raw.customer_contact.id,
                      name: raw.customer_contact.name,
                      title: raw.customer_contact.title,
                      phone: raw.customer_contact.phone,
                      email: raw.customer_contact.email,
                  },
        customerContactSource: raw.customer_contact_source ?? null,
        startDate: raw.start_date,
        startTime: raw.start_time,
        endDate: raw.end_date,
        endTime: raw.end_time,
        dateTbc: raw.date_tbc,
        isPickup: raw.is_pickup,
        loads: raw.billed_per_load && raw.loads ? mapDriverJobLoads(raw.loads) : null,
        driverName: raw.driver_name,
        driverAssignmentId: raw.job_worker_assignment_id,
        locations: raw.locations.map((location) => ({
            id: location.id,
            name: location.name,
            latitude: location.latitude,
            longitude: location.longitude,
            notes: location.notes,
        })),
        assetAssignments: raw.asset_assignments.map((assignment) => ({
            id: assignment.id,
            rentalAssetId: assignment.rental_asset_id,
            rentalAssetName: assignment.rental_asset_name,
            deployment: {
                status: assignment.deployment.status,
                locationLabel: assignment.deployment.location_label,
                deployedAt: assignment.deployment.deployed_at,
                confirmed: assignment.deployment.confirmed ?? true,
            },
        })),
        comments: raw.comments.map((comment) => ({
            id: comment.id,
            content: comment.content,
            createdAt: comment.created_at,
            createdByName: comment.created_by_name,
        })),
        documents: raw.documents.map((doc) => ({
            id: doc.id,
            name: doc.name,
            contentType: doc.content_type,
            sizeBytes: doc.size_bytes,
        })),
        stockEquipmentGroups: raw.stock_equipment_groups.map((group) => ({
            ...mapStockSource(group),
            rentalAssetTypeId: group.rental_asset_type_id,
            rentalAssetTypeName: group.rental_asset_type_name,
            assignedQuantity: group.assigned_quantity,
            deliveredQuantity: group.delivered_quantity,
            remainingQuantity: group.remaining_quantity,
            returnedQuantity: group.returned_quantity,
            deliveredByLocation: group.delivered_by_location.map((location) => ({
                jobLocationId: location.job_location_id,
                locationName: location.location_name,
                quantity: location.quantity,
            })),
        })),
    }
}
